import React, { useState, useEffect } from 'react';
import { usersApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  Plus, Users, Shield, Edit, Trash2, User,
  Mail, Key, Check, X
} from 'lucide-react';
import axios from 'axios';
import { getApiErrorMessage } from '../lib/errorUtils';

const ROLES = [
  { value: 'super_admin', label: 'Super Administrateur', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  { value: 'admin', label: 'Administrateur', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  { value: 'depot_manager', label: 'Responsable Dépôt', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  { value: 'warehouse_operator', label: 'Magasinier', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  { value: 'scan_operator', label: 'Opérateur Scan', color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
  { value: 'office', label: 'Bureau / Achats', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  { value: 'readonly', label: 'Lecture seule', color: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30' },
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'warehouse_operator',
  });
  const [userSearch, setUserSearch] = useState('');
  const [userSortBy, setUserSortBy] = useState('name');
  const [userSortOrder, setUserSortOrder] = useState('asc');
  const [userRoleFilter, setUserRoleFilter] = useState('all');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await usersApi.getAll();
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    try {
      await axios.post(`${API_URL}/api/auth/register`, formData, { withCredentials: true });
      toast.success('Utilisateur créé avec succès');
      setShowCreateDialog(false);
      fetchUsers();
      setFormData({
        email: '',
        password: '',
        name: '',
        role: 'warehouse_operator',
      });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la création'));
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();

    try {
      const updates = {
        name: formData.name,
        role: formData.role,
      };
      if (formData.password) {
        updates.password = formData.password;
      }

      await usersApi.update(selectedUser.id, updates);
      toast.success('Utilisateur modifié');
      setShowEditDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la modification'));
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await usersApi.update(user.id, { is_active: !user.is_active });
      toast.success(user.is_active ? 'Utilisateur désactivé' : 'Utilisateur activé');
      fetchUsers();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleDeactivateUser = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir désactiver cet utilisateur ?')) return;

    try {
      await usersApi.delete(userId);
      toast.success('Utilisateur désactivé');
      fetchUsers();
    } catch (error) {
      toast.error('Erreur lors de la désactivation');
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      password: '',
      name: user.name,
      role: user.role,
    });
    setShowEditDialog(true);
  };

  const getRoleBadge = (role) => {
    const roleConfig = ROLES.find(r => r.value === role);
    return (
      <Badge variant="outline" className={roleConfig?.color || ''}>
        {roleConfig?.label || role}
      </Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Gestion des Utilisateurs</h1>
          <p className="text-zinc-400 text-sm mt-1">{users.length} utilisateurs enregistrés</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="create-user-button">
              <Plus className="w-4 h-4 mr-2" />
              Nouvel Utilisateur
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white font-heading">Nouvel Utilisateur</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Nom complet *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white"
                  placeholder="Jean Dupont"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white"
                  placeholder="jean.dupont@example.com"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Mot de passe *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white"
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Rôle *</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {ROLES.map(role => (
                      <SelectItem key={role.value} value={role.value} className="text-white">
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-zinc-800">
                <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)} className="border-zinc-700">
                  Annuler
                </Button>
                <Button type="submit" className="bg-amber-500 hover:bg-amber-400 text-black">
                  Créer l'utilisateur
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search & Filter Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input value={userSearch} onChange={e => setUserSearch(e.target.value)}
            placeholder="Rechercher par nom, email..."
            className="w-full bg-zinc-900 border border-zinc-800 rounded-lg pl-10 pr-4 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-amber-500/50 focus:outline-none"
            data-testid="user-search" />
        </div>
        <select value={userRoleFilter} onChange={e => setUserRoleFilter(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-2 text-xs text-zinc-300">
          <option value="all">Tous les roles</option>
          {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <span className="text-zinc-600 text-xs">Tri:</span>
        <select value={userSortBy} onChange={e => setUserSortBy(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-2 text-xs text-zinc-300">
          <option value="name">Nom</option>
          <option value="email">Email</option>
          <option value="role">Role</option>
          <option value="created_at">Date</option>
        </select>
        <button onClick={() => setUserSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
          className="bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-2 text-xs text-zinc-300 hover:border-amber-500/50">
          {userSortOrder === 'asc' ? '↑ A-Z' : '↓ Z-A'}
        </button>
      </div>

      {/* Users Table */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-0">
          <ScrollArea className="h-[calc(100vh-340px)]">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400">Utilisateur</TableHead>
                  <TableHead className="text-zinc-400">Email</TableHead>
                  <TableHead className="text-zinc-400">Rôle</TableHead>
                  <TableHead className="text-zinc-400">Créé le</TableHead>
                  <TableHead className="text-zinc-400 text-center">Actif</TableHead>
                  <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : (() => {
                  let filtered = users;
                  if (userSearch) {
                    const q = userSearch.toLowerCase();
                    filtered = filtered.filter(u => u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q));
                  }
                  if (userRoleFilter !== 'all') filtered = filtered.filter(u => u.role === userRoleFilter);
                  filtered = [...filtered].sort((a, b) => {
                    let va = a[userSortBy] || '', vb = b[userSortBy] || '';
                    if (userSortBy === 'created_at') { va = new Date(va || 0); vb = new Date(vb || 0); }
                    if (va < vb) return userSortOrder === 'asc' ? -1 : 1;
                    if (va > vb) return userSortOrder === 'asc' ? 1 : -1;
                    return 0;
                  });
                  if (filtered.length === 0) return (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-zinc-500">
                      <Users className="w-12 h-12 mx-auto mb-2 opacity-50" /><p>Aucun utilisateur</p>
                    </TableCell></TableRow>
                  );
                  return filtered.map((user) => (
                    <TableRow key={user.id} className="border-zinc-800 table-row-hover">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
                            <User className="w-5 h-5 text-zinc-400" />
                          </div>
                          <div>
                            <p className="font-medium text-white">{user.name}</p>
                            <p className="text-xs text-zinc-500 font-mono">{user.id}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-zinc-400">{user.email}</TableCell>
                      <TableCell>{getRoleBadge(user.role)}</TableCell>
                      <TableCell className="text-zinc-400">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR') : '-'}
                      </TableCell>
                      <TableCell className="text-center">
                        <Switch
                          checked={user.is_active !== false}
                          onCheckedChange={() => handleToggleActive(user)}
                          disabled={user.id === currentUser?.id}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-zinc-700"
                            onClick={() => openEditDialog(user)}
                            disabled={user.role === 'super_admin' && currentUser?.role !== 'super_admin'}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                            onClick={() => handleDeactivateUser(user.id)}
                            disabled={user.id === currentUser?.id || (user.role === 'super_admin' && currentUser?.role !== 'super_admin')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ));
                })()}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">Modifier l'utilisateur</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditUser} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Nom complet</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="bg-zinc-950 border-zinc-800 text-white"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Email</Label>
              <Input
                value={formData.email}
                className="bg-zinc-950 border-zinc-800 text-zinc-500"
                disabled
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Nouveau mot de passe (optionnel)</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="bg-zinc-950 border-zinc-800 text-white"
                placeholder="Laisser vide pour ne pas changer"
                minLength={6}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Rôle</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {ROLES.map(role => (
                    <SelectItem key={role.value} value={role.value} className="text-white">
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t border-zinc-800">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)} className="border-zinc-700">
                Annuler
              </Button>
              <Button type="submit" className="bg-amber-500 hover:bg-amber-400 text-black">
                Enregistrer
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
